import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shine-beauty/artifacts/9gn465w9_Buttershine%20logo%20no%20bg.png";

export const FounderStory = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section 
      ref={sectionRef}
      id="story" 
      data-testid="story-section" 
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7] to-[#F5F2EA]" />
      
      {/* Parallax decorative elements */}
      <motion.div 
        style={{ y }}
        className="absolute top-20 right-10 w-64 h-64 rounded-full bg-[#DAB668]/5 blur-3xl"
      />
      <motion.div 
        style={{ y: useTransform(scrollYProgress, [0, 1], [50, -50]) }}
        className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-[#DAB668]/10 blur-2xl"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#DAB668] text-sm font-medium tracking-widest uppercase mb-4 block">
              Our Story
            </span>
            <h2 data-testid="story-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2C2C2C] mb-8 leading-tight">
              A Vision of 
              <span className="block gradient-text">Inclusive Beauty</span>
            </h2>
            
            <div className="space-y-6 text-[#6E6A62] leading-relaxed">
              <p>
                Buttershine was founded by <strong className="text-[#2C2C2C]">Varun Mohan</strong>, 
                with a vision to bring premium beauty and wellness tools to everyone — regardless 
                of age or gender.
              </p>
              <p>
                Built on the belief that <em>self-care is a lifestyle, not a luxury</em>, 
                Buttershine curates the finest gadgets and tools to help you glow from 
                the inside out.
              </p>
              <p>
                We believe everyone deserves access to the best beauty technology and 
                wellness tools. Our carefully selected collection combines ancient wisdom 
                with modern innovation.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 flex items-center gap-4"
            >
              <div className="w-16 h-16 rounded-full bg-[#DAB668]/10 flex items-center justify-center">
                <span className="text-2xl font-serif text-[#DAB668]">V</span>
              </div>
              <div>
                <p className="font-serif text-lg text-[#2C2C2C]">Varun Mohan</p>
                <p className="text-sm text-[#6E6A62]">Founder, Buttershine</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Visual Element */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative aspect-square max-w-md mx-auto">
              {/* Main circle */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#DAB668]/20 to-[#DAB668]/5 flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="Buttershine"
                  className="w-2/3 h-auto"
                  data-testid="story-logo"
                />
              </div>
              
              {/* Decorative rings */}
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-20px] rounded-full border border-[#DAB668]/20"
              />
              <motion.div 
                animate={{ rotate: -360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-40px] rounded-full border border-[#DAB668]/10"
              />
              
              {/* Floating elements */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute top-0 right-10 w-12 h-12 bg-[#DAB668]/20 rounded-full"
              />
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 5, repeat: Infinity }}
                className="absolute bottom-10 left-0 w-8 h-8 bg-[#DAB668]/30 rounded-full"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FounderStory;
